/**
 * <s-product-gallery> — Product photo gallery with arrows, thumbnails, fullscreen lightbox and zoom.
 *
 * Markup contract (all scoped inside the element):
 *   [data-gallery-track]          — horizontal scroll-snap track of main slides (native swipe on touch).
 *   [data-gallery-slide]          — one per media item; data-media-id links variants to slides.
 *   [data-gallery-counter]        — receives "current / total".
 *   [data-gallery-thumbs]         — scrollable thumbnail strip.
 *   [data-gallery-thumb]          — thumbnail buttons; data-index is the slide index.
 *   [data-gallery-prev|next]      — main arrows (disabled at the ends).
 *   dialog[data-gallery-lightbox] — fullscreen viewer inside <s-dialog>.
 *     [data-lightbox-track]       — scroll-snap track of fullscreen slides.
 *     [data-lightbox-slide]       — data-slide-index maps back to the main slide index.
 *     [data-lightbox-image]       — image that zooms on click/tap and pans on pointer move/drag.
 *     [data-lightbox-counter]     — receives "current / total".
 *
 * Commands (commandfor this element):
 *   --prev / --next               — step the main gallery.
 *   --goto                        — jump to event.source dataset.index.
 *   --open                        — open the lightbox at event.source dataset.index.
 *   --lightbox-prev / --lightbox-next / --zoom — lightbox navigation and zoom toggle.
 *
 * Dataset:
 *   data-variant-media            — JSON map of variant id to featured media id; option changes jump to that slide.
 *   data-initial-media            — media id to start on (set when the URL selects a variant).
 */
const ZOOM_SCALE = 2.5;

class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #thumbStrip;
  #counter;
  #prev;
  #next;
  #dialog;
  #lbTrack;
  #lbSlides = [];
  #lbCounter;
  #index = 0;
  #lbIndex = 0;
  #variantMedia = {};
  #zoomed = null;
  #drag = null;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#thumbStrip = this.querySelector('[data-gallery-thumbs]');
    this.#counter = this.querySelector('[data-gallery-counter]');
    this.#prev = this.querySelector('[data-gallery-prev]');
    this.#next = this.querySelector('[data-gallery-next]');
    this.#dialog = this.querySelector('dialog[data-gallery-lightbox]');
    this.#lbTrack = this.querySelector('[data-lightbox-track]');
    this.#lbSlides = [...this.querySelectorAll('[data-lightbox-slide]')];
    this.#lbCounter = this.querySelector('[data-lightbox-counter]');

    try {
      this.#variantMedia = JSON.parse(this.dataset.variantMedia || '{}');
    } catch {
      this.#variantMedia = {};
    }

    if (!this.#track || this.#slides.length === 0) return;

    this.addEventListener('command', this.#onCommand, opts);
    this.#track.addEventListener('scroll', this.#onTrackScroll, { ...opts, passive: true });
    this.#lbTrack?.addEventListener('scroll', this.#onLightboxScroll, { ...opts, passive: true });
    this.#dialog?.addEventListener('keydown', this.#onLightboxKey, opts);
    this.#dialog?.addEventListener('close', () => this.#resetZoom(), opts);
    this.#track.addEventListener('keydown', this.#onTrackKey, opts);

    for (const image of this.querySelectorAll('[data-lightbox-image]')) {
      image.addEventListener('pointerdown', this.#onZoomPointerDown, opts);
      image.addEventListener('pointermove', this.#onZoomPointerMove, opts);
      image.addEventListener('pointerup', this.#onZoomPointerUp, opts);
      image.addEventListener('pointercancel', this.#onZoomPointerCancel, opts);
    }

    document.addEventListener('change', this.#onVariantChange, opts);

    // A ?variant= link opens on that variant's photo.
    const initial = this.dataset.initialMedia
      ? this.#slides.findIndex((slide) => slide.dataset.mediaId === this.dataset.initialMedia)
      : -1;
    if (initial > 0) {
      requestAnimationFrame(() => this.#goTo(initial, 'instant'));
    }

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- helpers ---------- */

  get #rtlSign() {
    return getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
  }

  get #behavior() {
    return this.#reduceMotion.matches ? 'instant' : 'smooth';
  }

  #indexFromScroll(track) {
    const width = track.clientWidth || 1;
    return Math.round(Math.abs(track.scrollLeft) / width);
  }

  #scrollTrack(track, index, behavior) {
    track.scrollTo({ left: index * track.clientWidth * this.#rtlSign, behavior });
  }

  /* ---------- main gallery ---------- */

  #goTo(index, behavior = this.#behavior) {
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    this.#index = clamped;
    this.#scrollTrack(this.#track, clamped, behavior);
    this.#render();
  }

  #render() {
    const total = this.#slides.length;
    if (this.#counter) this.#counter.textContent = `${this.#index + 1} / ${total}`;
    if (this.#prev) this.#prev.disabled = this.#index === 0;
    if (this.#next) this.#next.disabled = this.#index === total - 1;

    this.#slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== this.#index);
    });

    this.#thumbs.forEach((thumb) => {
      const active = Number(thumb.dataset.index) === this.#index;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');

      if (active && this.#thumbStrip) {
        const strip = this.#thumbStrip;
        // The strip is position: relative, so offsetLeft is measured within its scrollable content.
        const start = thumb.offsetLeft;
        const end = start + thumb.offsetWidth;
        if (start < strip.scrollLeft || end > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: start - strip.clientWidth / 2 + thumb.offsetWidth / 2, behavior: this.#behavior });
        }
      }
    });
  }

  #onTrackScroll = () => {
    const index = this.#indexFromScroll(this.#track);
    if (index !== this.#index) {
      this.#index = index;
      this.#render();
    }
  };

  #onTrackKey = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#goTo(this.#index + this.#rtlSign);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#goTo(this.#index - this.#rtlSign);
    }
  };

  #onVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const form = target.closest('form');
    if (!form || !form.querySelector('input[name="id"]')) return;

    const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
    const mediaId = variantId ? this.#variantMedia[variantId] : null;
    if (!mediaId) return;

    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.#goTo(index);
  };

  /* ---------- commands ---------- */

  #onCommand = (event) => {
    const source = event.source instanceof HTMLElement ? event.source : null;
    switch (event.command) {
      case '--prev':
        this.#goTo(this.#index - 1);
        break;
      case '--next':
        this.#goTo(this.#index + 1);
        break;
      case '--goto':
        if (source) this.#goTo(Number(source.dataset.index));
        break;
      case '--open':
        this.#openLightbox(source?.dataset.index !== undefined ? Number(source.dataset.index) : this.#index);
        break;
      case '--lightbox-prev':
        this.#lightboxGoTo(this.#lbIndex - 1);
        break;
      case '--lightbox-next':
        this.#lightboxGoTo(this.#lbIndex + 1);
        break;
      case '--zoom':
        this.#toggleZoom(this.#lbSlides[this.#lbIndex]?.querySelector('[data-lightbox-image]'), 50, 50);
        break;
    }
  };

  /* ---------- lightbox ---------- */

  #openLightbox(slideIndex) {
    if (!this.#dialog || !this.#lbTrack || this.#lbSlides.length === 0) return;

    let lbIndex = this.#lbSlides.findIndex((slide) => Number(slide.dataset.slideIndex) === slideIndex);
    if (lbIndex < 0) lbIndex = 0;

    if (!this.#dialog.open) this.#dialog.showModal();
    this.#lbIndex = lbIndex;
    this.#loadLightboxImage(lbIndex);
    this.#loadLightboxImage(lbIndex + 1);
    requestAnimationFrame(() => {
      this.#scrollTrack(this.#lbTrack, lbIndex, 'instant');
      this.#renderLightbox();
    });
  }

  #loadLightboxImage(index) {
    const image = this.#lbSlides[index]?.querySelector('[data-lightbox-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';
  }

  #lightboxGoTo(index) {
    const clamped = Math.max(0, Math.min(this.#lbSlides.length - 1, index));
    this.#resetZoom();
    this.#lbIndex = clamped;
    this.#loadLightboxImage(clamped);
    this.#loadLightboxImage(clamped + 1);
    this.#scrollTrack(this.#lbTrack, clamped, this.#behavior);
    this.#renderLightbox();
  }

  #renderLightbox() {
    const total = this.#lbSlides.length;
    if (this.#lbCounter) this.#lbCounter.textContent = `${this.#lbIndex + 1} / ${total}`;
    for (const button of this.#dialog.querySelectorAll('[data-lightbox-prev]')) button.disabled = this.#lbIndex === 0;
    for (const button of this.#dialog.querySelectorAll('[data-lightbox-next]')) button.disabled = this.#lbIndex === total - 1;

    // Keep the main gallery in step so closing lands on the photo the shopper was viewing.
    const slideIndex = Number(this.#lbSlides[this.#lbIndex]?.dataset.slideIndex);
    if (!Number.isNaN(slideIndex) && slideIndex !== this.#index) this.#goTo(slideIndex, 'instant');
  }

  #onLightboxScroll = () => {
    if (this.#zoomed) return;
    const index = this.#indexFromScroll(this.#lbTrack);
    if (index !== this.#lbIndex) {
      this.#lbIndex = index;
      this.#loadLightboxImage(index + 1);
      this.#renderLightbox();
    }
  };

  #onLightboxKey = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#lightboxGoTo(this.#lbIndex + this.#rtlSign);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#lightboxGoTo(this.#lbIndex - this.#rtlSign);
    }
  };

  /* ---------- zoom ---------- */

  #setOrigin(image, x, y) {
    const ox = Math.max(0, Math.min(100, x));
    const oy = Math.max(0, Math.min(100, y));
    image.dataset.originX = String(ox);
    image.dataset.originY = String(oy);
    image.style.transformOrigin = `${ox}% ${oy}%`;
  }

  #toggleZoom(image, x, y) {
    if (!(image instanceof HTMLElement)) return;
    if (this.#zoomed === image) {
      this.#resetZoom();
      return;
    }
    this.#resetZoom();
    this.#zoomed = image;
    this.#setOrigin(image, x, y);
    image.style.transform = `scale(${ZOOM_SCALE})`;
    image.style.cursor = 'zoom-out';
    image.style.touchAction = 'none';
    this.#lbTrack.style.overflowX = 'hidden';
    this.#dialog.querySelector('[data-lightbox-zoom]')?.setAttribute('aria-pressed', 'true');
  }

  #resetZoom() {
    const image = this.#zoomed;
    this.#zoomed = null;
    this.#drag = null;
    if (image) {
      image.style.transform = '';
      image.style.cursor = '';
      image.style.touchAction = '';
    }
    if (this.#lbTrack) this.#lbTrack.style.overflowX = '';
    this.#dialog?.querySelector('[data-lightbox-zoom]')?.setAttribute('aria-pressed', 'false');
  }

  #pointerPercent(image, event) {
    const rect = image.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    };
  }

  #onZoomPointerDown = (event) => {
    const image = event.currentTarget;
    this.#drag = { x: event.clientX, y: event.clientY, moved: false, image };
    if (this.#zoomed === image && event.pointerType !== 'mouse') {
      image.setPointerCapture(event.pointerId);
    }
  };

  #onZoomPointerMove = (event) => {
    const image = event.currentTarget;
    if (this.#zoomed !== image) {
      if (this.#drag && Math.hypot(event.clientX - this.#drag.x, event.clientY - this.#drag.y) > 8) this.#drag.moved = true;
      return;
    }

    if (event.pointerType === 'mouse') {
      // Desktop: the zoomed view follows the cursor.
      const { x, y } = this.#pointerPercent(image, event);
      this.#setOrigin(image, x, y);
      return;
    }

    if (!this.#drag) return;
    // Touch: drag to pan. Moving the origin against the finger reveals the opposite side.
    const rect = image.getBoundingClientRect();
    const baseWidth = rect.width / ZOOM_SCALE;
    const baseHeight = rect.height / ZOOM_SCALE;
    const dx = event.clientX - this.#drag.x;
    const dy = event.clientY - this.#drag.y;
    if (Math.hypot(dx, dy) > 4) this.#drag.moved = true;
    const ox = Number(image.dataset.originX || 50) - (dx / (baseWidth * (ZOOM_SCALE - 1))) * 100;
    const oy = Number(image.dataset.originY || 50) - (dy / (baseHeight * (ZOOM_SCALE - 1))) * 100;
    this.#setOrigin(image, ox, oy);
    this.#drag.x = event.clientX;
    this.#drag.y = event.clientY;
  };

  #onZoomPointerUp = (event) => {
    const image = event.currentTarget;
    const drag = this.#drag;
    this.#drag = null;
    if (drag?.moved) return;
    const { x, y } = this.#pointerPercent(image, event);
    this.#toggleZoom(image, x, y);
  };

  #onZoomPointerCancel = () => {
    this.#drag = null;
  };
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
